/**
 * Unlock — PIN entry (and WebAuthn when the device was paired with it).
 * Wrong PIN: increment attempts; >=5 failures wipe the device record
 * and force re-pair.
 *
 * Premium polish: brand mark + 6 PIN dots with pulse-on-fill + fingerprint
 * icon on the biometric CTA + haptic feedback.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Fingerprint, Loader2, Smartphone, Lock } from 'lucide-react';
import {
  TooManyPinAttemptsError,
  WrongPinError,
  WebAuthnFailedError,
  NoDeviceError,
  unlock,
} from '@argos/auth-client';
import { authHttp } from '../api/client';
import { DexieDeviceStore } from '../auth/dexie-store';
import { setSession } from '../auth/session';
import { getDb, wipeDb } from '../db';
import { runBootstrapSync } from '../sync/bootstrap';
import * as haptics from '../lib/haptics';

const PWA_HOSTNAME =
  typeof window !== 'undefined' ? window.location.hostname : 'localhost';
const RP_ID = PWA_HOSTNAME;

export function UnlockPage() {
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const [unlockMethod, setUnlockMethod] = useState<'pin' | 'webauthn' | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const dev = await getDb().device.get('self');
        if (cancelled) return;
        if (!dev) {
          navigate('/pair', { replace: true });
          return;
        }
        setUnlockMethod(dev.unlock_method);
        const u = dev.user as { email?: string } | undefined;
        setUserEmail(u?.email ?? null);
      } catch {
        if (!cancelled) navigate('/pair', { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      haptics.tap();
      const session = await unlock({
        store: new DexieDeviceStore(),
        http: authHttp,
        rpId: RP_ID,
        pin: unlockMethod === 'pin' ? pin : undefined,
      });
      haptics.success();
      setSession(session);
      // Best-effort bootstrap. Don't block unlock on a failed
      // /sync/me — events page handles empty state.
      void runBootstrapSync().catch(() => undefined);
      navigate('/events', { replace: true });
    } catch (err) {
      haptics.error();
      if (err instanceof WrongPinError) {
        setError('Wrong PIN.');
        setAttemptsRemaining(err.attemptsRemaining);
        setPin('');
      } else if (err instanceof TooManyPinAttemptsError) {
        await wipeDb();
        navigate('/pair', { replace: true });
      } else if (err instanceof NoDeviceError) {
        navigate('/pair', { replace: true });
      } else if (err instanceof WebAuthnFailedError) {
        setError(`Biometric failed: ${err.message}`);
      } else {
        setError(err instanceof Error ? err.message : 'Unlock failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (unlockMethod == null) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 gap-3 text-mono-400">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Brand mark */}
        <div className="flex flex-col items-center mb-7">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-cyan-400 to-cyan-700 shadow-glow flex items-center justify-center">
            <Smartphone className="h-6 w-6 text-mono-950" strokeWidth={2.4} />
          </div>
          <p className="mt-2 text-[10px] tracking-[0.3em] uppercase text-mono-400">ARGOS Field</p>
        </div>

        <h1 className="text-2xl font-bold text-mono-100 tracking-tight">Welcome back</h1>
        {userEmail && (
          <p className="text-mono-400 text-sm mt-1">Signed in as <span className="text-mono-200">{userEmail}</span></p>
        )}

        {unlockMethod === 'pin' ? (
          <form onSubmit={submit} className="space-y-4 mt-6">
            {/* Six dot indicators that fill as the user types */}
            <div className="flex justify-center gap-2.5 mb-2" aria-hidden>
              {Array.from({ length: 6 }).map((_, i) => (
                <span
                  key={i}
                  className={`h-3 w-3 rounded-full transition-all duration-150 ${
                    i < pin.length
                      ? 'bg-cyan-400 scale-110 shadow-glow'
                      : 'bg-mono-800 ring-1 ring-mono-700'
                  }`}
                />
              ))}
            </div>
            <label className="block">
              <span className="sr-only">6-digit PIN</span>
              <input
                type="password"
                inputMode="numeric"
                maxLength={6}
                autoFocus
                className="input-field tracking-[0.5em] text-center text-xl font-semibold"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                placeholder="••••••"
                aria-label="6-digit PIN"
              />
            </label>

            {error && (
              <div className="text-accent-red text-sm border border-red-900/40 rounded-md p-2.5 bg-red-950/20">
                {error}
                {attemptsRemaining != null && (
                  <span className="block text-mono-400 mt-1 text-xs">
                    {attemptsRemaining} attempt{attemptsRemaining === 1 ? '' : 's'} remaining
                    before this device wipes itself.
                  </span>
                )}
              </div>
            )}

            <button
              type="submit"
              className="btn-primary w-full inline-flex items-center justify-center gap-1.5"
              disabled={submitting || pin.length !== 6}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.4} /> : <Lock className="h-4 w-4" strokeWidth={2.4} />}
              {submitting ? 'Unlocking…' : 'Unlock'}
            </button>
          </form>
        ) : (
          <div className="space-y-4 mt-6">
            <div className="rounded-xl border border-mono-800 bg-mono-900/40 p-5 flex flex-col items-center gap-3">
              <div className="h-14 w-14 rounded-full bg-cyan-500/15 ring-2 ring-cyan-500/40 flex items-center justify-center">
                <Fingerprint className="h-7 w-7 text-cyan-300" strokeWidth={1.8} />
              </div>
              <p className="text-mono-300 text-sm text-center">
                Use Face ID, Touch ID, or your fingerprint to unlock.
              </p>
            </div>
            {error && (
              <div className="text-accent-red text-sm border border-red-900/40 rounded-md p-2.5 bg-red-950/20">
                {error}
              </div>
            )}
            <button
              type="button"
              className="btn-primary w-full inline-flex items-center justify-center gap-1.5"
              disabled={submitting}
              onClick={() => submit()}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.4} /> : <Fingerprint className="h-4 w-4" strokeWidth={2.4} />}
              {submitting ? 'Verifying…' : 'Unlock with biometric'}
            </button>
          </div>
        )}

        <button
          type="button"
          className="btn-secondary w-full mt-6 text-mono-400 text-sm"
          onClick={async () => {
            if (confirm('Forget this device? You will need to re-pair.')) {
              await wipeDb();
              navigate('/pair', { replace: true });
            }
          }}
        >
          Forget this device
        </button>
      </div>
    </div>
  );
}
